/**
 * Impact Dog Crates - Anxiety Crate Quiz
 *
 * Retargeting quiz behavior, breed lookup, diagnosis, High Anxiety sizing,
 * attribution, and Klaviyo lead capture. The implementation is section-scoped
 * so it supports multiple instances and Shopify theme-editor reloads.
 */

(function() {
  'use strict';

  if (window.__impactAnxietyCrateQuiz) {
    window.__impactAnxietyCrateQuiz.init(document);
    return;
  }

  const QUESTION_COUNT = 8;
  const RESULTS_STEP = 9;
  const BREED_RESULT_LIMIT = 10;
  const BREED_SEARCH_DELAY = 150;
  const KLAVIYO_REVISION = '2026-07-15';
  const KLAVIYO_REQUEST_TIMEOUT = 10000;

  const WEIGHT_TO_SIZE = {
    'under-25': '24',
    '25-50': '30',
    '50-85': '34',
    '85-150': '40+',
    'over-150': '54'
  };

  const SIZE_LABELS = {
    '20': '20"',
    '24': '24"',
    '30': '30"',
    '34': '34"',
    '40': '40"',
    '40+': '40"+',
    '48': '48"',
    '48+': '48"+',
    '54': '54"'
  };

  const breedRequests = new Map();

  function interpolate(template, values = {}) {
    return Object.entries(values).reduce((result, [key, value]) => {
      return result.split(`[${key}]`).join(String(value));
    }, template || '');
  }

  function normalizeName(value) {
    return value.trim().replace(/\s+/g, ' ');
  }

  function toAbsoluteHttpUrl(value) {
    const rawUrl = String(value || '').trim();
    if (!rawUrl) return '';

    try {
      const url = new URL(rawUrl, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
    } catch {
      return '';
    }
  }

  function decodeHtmlEntities(value) {
    if (typeof value !== 'string' || !value.includes('&')) return value;

    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    return decoder.value;
  }

  function decodeTranslationTree(value) {
    if (typeof value === 'string') return decodeHtmlEntities(value);
    if (Array.isArray(value)) return value.map(item => decodeTranslationTree(item));
    if (!value || typeof value !== 'object') return value;

    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, decodeTranslationTree(item)])
    );
  }

  function createSubmissionId() {
    if (typeof window.crypto?.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return `anxiety-quiz-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function extractSize(rawSize) {
    if (!rawSize || /varies/i.test(rawSize)) return null;

    const match = String(rawSize).match(/([0-9]+)\s*["″”]?\s*(\+)?/);
    if (!match) return null;

    return `${match[1]}${match[2] ? '+' : ''}`;
  }

  function requestBreedData(url) {
    if (!url) return Promise.reject(new Error('Breed data URL is missing.'));

    if (!breedRequests.has(url)) {
      const request = fetch(url, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Breed data request failed with ${response.status}.`);
          }
          return response.json();
        })
        .then(data => {
          if (!Array.isArray(data)) {
            throw new Error('Breed data response is not an array.');
          }
          return data;
        })
        .catch(error => {
          breedRequests.delete(url);
          throw error;
        });

      breedRequests.set(url, request);
    }

    return breedRequests.get(url);
  }

  function searchBreeds(database, query, limit = BREED_RESULT_LIMIT) {
    if (!Array.isArray(database) || query.length < 2) return [];

    const normalizedQuery = query.toLocaleLowerCase();
    const matches = [];
    const seen = new Set();

    const addMatch = breed => {
      const name = breed['Dog breed'];
      if (!name || seen.has(name)) return;

      seen.add(name);
      matches.push({
        name,
        rawSize: breed['Impact Dog Crate Size'] || '',
        size: extractSize(breed['Impact Dog Crate Size'])
      });
    };

    database.forEach(breed => {
      const name = String(breed['Dog breed'] || '').toLocaleLowerCase();
      if (name.startsWith(normalizedQuery)) addMatch(breed);
    });

    database.forEach(breed => {
      const name = String(breed['Dog breed'] || '').toLocaleLowerCase();
      if (name.includes(normalizedQuery)) addMatch(breed);
    });

    database.forEach(breed => {
      const aliases = Array.isArray(breed.Aliases) ? breed.Aliases : [];
      if (aliases.some(alias => String(alias).toLocaleLowerCase().includes(normalizedQuery))) {
        addMatch(breed);
      }
    });

    return matches.slice(0, limit);
  }

  function createInitialState() {
    return {
      submissionId: createSubmissionId(),
      currentStep: 1,
      answers: {
        dogName: '',
        behavior: '',
        trigger: '',
        history: '',
        breed: '',
        breedSize: '',
        weight: '',
        worst: '',
        email: '',
        marketingConsent: false
      },
      diagnosis: null,
      recommendation: null
    };
  }

  class AnxietyCrateQuiz {
    constructor(root) {
      this.root = root;
      this.config = this.parseConfig();
      this.state = createInitialState();
      this.abortController = new AbortController();
      this.requestControllers = new Set();
      this.announcementFrame = null;
      this.breedDatabase = [];
      this.breedLoaded = false;
      this.breedLoadFailed = false;
      this.breedOptions = [];
      this.breedActiveIndex = -1;
      this.breedSearchTimer = null;
      this.breedRequestToken = 0;
      this.submitting = false;
      this.submissionControls = [];
      this.started = false;
      this.elements = this.cacheElements();

      if (!this.elements.form || this.elements.steps.length === 0) return;

      this.bindEvents();
      this.loadBreedData();
      this.showStep(1, {
        announce: false,
        focus: false,
        scroll: false,
        track: false
      });
    }

    parseConfig() {
      const configElement = this.root.querySelector('[data-anxiety-quiz-config]');
      if (!configElement) return {};

      try {
        const config = JSON.parse(configElement.textContent);
        config.i18n = decodeTranslationTree(config.i18n);
        return config;
      } catch (error) {
        console.error('Anxiety Crate Quiz configuration could not be parsed.', error);
        return {};
      }
    }

    cacheElements() {
      return {
        form: this.root.querySelector('[data-quiz-form]'),
        steps: Array.from(this.root.querySelectorAll('.quiz-step')),
        progressBar: this.root.querySelector('[data-progress-bar]'),
        progressFill: this.root.querySelector('[data-progress-fill]'),
        progressText: this.root.querySelector('[data-progress-text]'),
        quizStatus: this.root.querySelector('[data-quiz-status]'),
        dogNameInput: this.root.querySelector('[data-dog-name-input]'),
        breedInput: this.root.querySelector('[data-breed-input]'),
        breedDropdown: this.root.querySelector('[data-breed-dropdown]'),
        breedStatus: this.root.querySelector('[data-breed-status]'),
        breedValue: this.root.querySelector('[data-breed-value]'),
        mixedBreedButton: this.root.querySelector('[data-select-mixed]'),
        emailInput: this.root.querySelector('[data-email-input]'),
        marketingConsentInput: this.root.querySelector('[data-marketing-consent]'),
        completeButton: this.root.querySelector('[data-complete-quiz]'),
        resultsTitle: this.root.querySelector('[data-results-title]'),
        resultsIntro: this.root.querySelector('[data-results-intro]'),
        diagnosisTitle: this.root.querySelector('[data-diagnosis-title]'),
        diagnosisCopy: this.root.querySelector('[data-diagnosis-copy]'),
        resultSize: this.root.querySelector('[data-result-size]'),
        sizeCaveat: this.root.querySelector('[data-size-caveat]'),
        resultCta: this.root.querySelector('[data-result-cta]'),
        resultImage: this.root.querySelector('.quiz-result-card__product-image')
      };
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.elements.form.addEventListener('submit', event => this.handleSubmit(event), { signal });

      this.root.addEventListener('click', event => {
        const backButton = event.target.closest('[data-back]');
        if (backButton) {
          if (this.submitting) return;
          this.showStep(Math.max(1, this.state.currentStep - 1));
          return;
        }

        const mixedButton = event.target.closest('[data-select-mixed]');
        if (mixedButton) {
          this.selectBreed('mixed', '', '');
          return;
        }

        const retakeButton = event.target.closest('[data-retake-quiz]');
        if (retakeButton) {
          if (this.submitting) return;
          this.reset();
          return;
        }

        const resultCta = event.target.closest('[data-result-cta]');
        if (resultCta) {
          this.track('offer_clicked', this.publicResultData());
        }
      }, { signal });

      this.root.addEventListener('change', event => {
        const marketingConsent = event.target.closest('[data-marketing-consent]');
        if (marketingConsent) {
          this.state.answers.marketingConsent = marketingConsent.checked;
          return;
        }

        const input = event.target.closest('[data-answer]');
        if (!input) return;

        const question = input.dataset.answer;
        this.state.answers[question] = input.value;
        this.syncRadioStyles(input.name);
        this.clearStepError(this.currentStepElement());
      }, { signal });

      this.elements.dogNameInput?.addEventListener('input', () => {
        this.markStarted();
        this.state.answers.dogName = normalizeName(this.elements.dogNameInput.value);
        this.updateDogNameTemplates();
        if (this.state.answers.dogName) this.clearStepError(this.currentStepElement());
      }, { signal });

      this.elements.emailInput?.addEventListener('input', () => {
        this.state.answers.email = this.elements.emailInput.value.trim();
        if (this.elements.emailInput.validity.valid && this.state.answers.email) {
          this.clearStepError(this.currentStepElement());
        }
      }, { signal });

      this.elements.breedInput?.addEventListener('input', () => this.handleBreedInput(), { signal });
      this.elements.breedInput?.addEventListener('keydown', event => this.handleBreedKeydown(event), { signal });
      this.elements.breedInput?.addEventListener('focus', () => {
        const query = this.elements.breedInput.value.trim();
        if (query.length >= 2) this.showBreedResults(query);
      }, { signal });

      document.addEventListener('click', event => {
        const breedSearch = this.elements.breedInput?.closest('.quiz-breed-search');
        if (breedSearch && !breedSearch.contains(event.target)) this.hideBreedDropdown();
      }, { signal });
    }

    loadBreedData() {
      this.breedPromise = requestBreedData(this.config.breedDataUrl)
        .then(database => {
          this.breedDatabase = database;
          this.breedLoaded = true;
          this.breedLoadFailed = false;
          return database;
        })
        .catch(error => {
          this.breedLoadFailed = true;
          console.error('Anxiety Crate Quiz breed data failed to load.', error);
          return [];
        });
    }

    handleSubmit(event) {
      event.preventDefault();
      this.markStarted();

      const step = this.currentStepElement();
      if (!step || this.state.currentStep > QUESTION_COUNT) return;

      if (!this.validateStep(step, true)) {
        this.focusInvalidField(step);
        return;
      }

      this.trackCurrentAnswer(step);

      if (this.state.currentStep === QUESTION_COUNT) {
        this.completeQuiz();
        return;
      }

      this.showStep(this.state.currentStep + 1);
    }

    currentStepElement() {
      return this.root.querySelector(`[data-step="${this.state.currentStep}"]`);
    }

    showStep(stepNumber, options = {}) {
      const settings = {
        announce: true,
        focus: true,
        scroll: true,
        track: true,
        ...options
      };
      let activeStep = null;

      this.elements.steps.forEach(step => {
        const isActive = Number(step.dataset.step) === stepNumber;
        step.classList.toggle('quiz-step--active', isActive);
        if (isActive) {
          activeStep = step;
          step.hidden = false;
          step.removeAttribute('inert');
          step.setAttribute('data-step-active', '');
        } else {
          step.hidden = true;
          step.setAttribute('inert', '');
          step.removeAttribute('data-step-active');
        }
      });

      this.hideBreedDropdown();
      this.state.currentStep = stepNumber;
      const progressMessage = this.updateProgress();

      if (settings.scroll) {
        this.root.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }

      if (settings.focus && activeStep) {
        const heading = activeStep.querySelector('[data-step-heading]');
        requestAnimationFrame(() => heading?.focus({ preventScroll: true }));
      }

      if (settings.announce) this.announce(this.elements.quizStatus, progressMessage);

      if (settings.track && stepNumber <= QUESTION_COUNT) {
        this.track('step_view', { question_number: stepNumber });
      }
    }

    updateProgress() {
      const isComplete = this.state.currentStep === RESULTS_STEP;
      const currentQuestion = Math.min(this.state.currentStep, QUESTION_COUNT);
      const message = isComplete
        ? this.config.i18n?.complete
        : interpolate(this.config.i18n?.questionProgress, {
          current: currentQuestion,
          total: QUESTION_COUNT
        });
      const percentage = isComplete ? 100 : (currentQuestion / QUESTION_COUNT) * 100;

      if (this.elements.progressFill) this.elements.progressFill.style.width = `${percentage}%`;
      if (this.elements.progressText) this.elements.progressText.textContent = message;
      if (this.elements.progressBar) {
        this.elements.progressBar.setAttribute('aria-valuenow', String(currentQuestion));
        this.elements.progressBar.setAttribute('aria-valuetext', message);
      }

      return message;
    }

    validateStep(step, showError = false) {
      const question = step.dataset.questionKey;
      let valid = false;

      switch (question) {
        case 'dogName':
          this.state.answers.dogName = normalizeName(this.elements.dogNameInput?.value || '');
          valid = this.state.answers.dogName.length > 0;
          break;
        case 'breed':
          valid = Boolean(this.state.answers.breed);
          break;
        case 'email':
          this.state.answers.email = this.elements.emailInput?.value.trim() || '';
          valid = Boolean(this.state.answers.email) && Boolean(this.elements.emailInput?.validity.valid);
          break;
        default:
          valid = Boolean(this.state.answers[question]);
      }

      if (valid) {
        this.clearStepError(step);
      } else if (showError) {
        this.showStepError(step, question);
      }

      return valid;
    }

    showStepError(step, question) {
      const error = step.querySelector('[data-step-error]');
      const fieldset = step.querySelector('fieldset');
      const messages = {
        dogName: this.config.i18n?.nameError,
        breed: this.config.i18n?.breedError,
        email: this.config.i18n?.emailError,
        capture: this.config.i18n?.captureError
      };

      if (error) {
        error.textContent = messages[question] || this.config.i18n?.choiceError || '';
        error.hidden = false;
      }
      fieldset?.setAttribute('aria-invalid', 'true');

      if (question === 'dogName') this.elements.dogNameInput?.setAttribute('aria-invalid', 'true');
      if (question === 'breed') this.elements.breedInput?.setAttribute('aria-invalid', 'true');
      if (question === 'email') this.elements.emailInput?.setAttribute('aria-invalid', 'true');
    }

    clearStepError(step) {
      if (!step) return;

      const error = step.querySelector('[data-step-error]');
      const fieldset = step.querySelector('fieldset');
      if (error) error.hidden = true;
      fieldset?.removeAttribute('aria-invalid');
      step.querySelectorAll('[aria-invalid="true"]').forEach(field => {
        field.setAttribute('aria-invalid', 'false');
      });
    }

    focusInvalidField(step) {
      const question = step.dataset.questionKey;
      if (question === 'dogName') {
        this.elements.dogNameInput?.focus();
      } else if (question === 'breed') {
        this.elements.breedInput?.focus();
      } else if (question === 'email') {
        this.elements.emailInput?.focus();
      } else {
        step.querySelector('[data-answer]')?.focus();
      }
    }

    syncRadioStyles(name) {
      if (!name) return;
      this.root.querySelectorAll(`input[name="${CSS.escape(name)}"]`).forEach(input => {
        input.closest('.quiz-option')?.classList.toggle('quiz-option--selected', input.checked);
      });
    }

    trackCurrentAnswer(step) {
      const question = step.dataset.questionKey;
      const answer = ['dogName', 'email'].includes(question)
        ? 'provided'
        : this.state.answers[question];

      this.track('answer', {
        question_number: this.state.currentStep,
        question,
        answer
      });
    }

    markStarted() {
      if (this.started) return;

      this.started = true;
      this.track('started');
      this.track('step_view', { question_number: 1 });
    }

    updateDogNameTemplates() {
      const dogName = this.state.answers.dogName || '';
      if (!dogName) return;

      this.root.querySelectorAll('[data-dog-template]').forEach(element => {
        element.textContent = interpolate(decodeHtmlEntities(element.dataset.dogTemplate), { dog: dogName });
      });
    }

    handleBreedInput() {
      clearTimeout(this.breedSearchTimer);
      const query = this.elements.breedInput.value.trim();

      if (this.state.answers.breed) {
        this.state.answers.breed = '';
        this.state.answers.breedSize = '';
        if (this.elements.breedValue) this.elements.breedValue.value = '';
        this.elements.mixedBreedButton?.setAttribute('aria-pressed', 'false');
        this.elements.breedInput.placeholder = this.config.i18n?.breedPlaceholder || '';
      }

      this.clearStepError(this.currentStepElement());

      if (query.length < 2) {
        this.hideBreedDropdown();
        if (query.length === 1) this.announce(this.elements.breedStatus, this.config.i18n?.breedMinimum || '');
        return;
      }

      this.breedSearchTimer = setTimeout(() => this.showBreedResults(query), BREED_SEARCH_DELAY);
    }

    async showBreedResults(query) {
      const normalizedQuery = query.trim();
      const requestToken = ++this.breedRequestToken;
      if (normalizedQuery.length < 2) {
        this.hideBreedDropdown();
        return;
      }

      if (!this.breedLoaded && !this.breedLoadFailed) {
        this.renderBreedMessage(this.config.i18n?.breedLoading || '');
        this.setBreedDropdownExpanded(true);
        await this.breedPromise;
      }

      if (requestToken !== this.breedRequestToken) return;

      if (this.breedLoadFailed) {
        this.renderBreedMessage(this.config.i18n?.breedLoadError || '');
        this.setBreedDropdownExpanded(true);
        this.announce(this.elements.breedStatus, this.config.i18n?.breedLoadError || '');
        return;
      }

      if (normalizedQuery !== this.elements.breedInput.value.trim()) return;

      const matches = searchBreeds(this.breedDatabase, normalizedQuery);
      if (matches.length === 0) {
        const message = interpolate(this.config.i18n?.breedNoResults, { query: normalizedQuery });
        this.renderBreedMessage(message);
        this.announce(this.elements.breedStatus, message);
        this.setBreedDropdownExpanded(true);
        return;
      }

      const fragment = document.createDocumentFragment();
      matches.forEach((match, index) => {
        const option = document.createElement('div');
        const name = document.createElement('span');
        const size = document.createElement('span');

        option.id = `${this.elements.breedDropdown.id}-option-${index}`;
        option.className = 'quiz-breed-search__item';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.breed = match.name;
        option.dataset.size = match.size || '';

        name.className = 'quiz-breed-search__name';
        name.textContent = match.name;
        size.className = 'quiz-breed-search__size';
        size.textContent = SIZE_LABELS[match.size] || match.rawSize;

        option.append(name, size);
        option.addEventListener('click', () => this.selectBreed(match.name, match.name, match.size || ''), {
          signal: this.abortController.signal
        });
        option.addEventListener('mousemove', () => this.setActiveBreedOption(index), {
          signal: this.abortController.signal
        });
        fragment.append(option);
      });

      this.elements.breedDropdown.replaceChildren(fragment);
      this.breedOptions = Array.from(this.elements.breedDropdown.querySelectorAll('[role="option"]'));
      this.resetBreedActiveOption();
      this.setBreedDropdownExpanded(true);

      const message = interpolate(this.config.i18n?.breedSuggestions, { count: matches.length });
      this.announce(this.elements.breedStatus, message);
    }

    renderBreedMessage(message) {
      const element = document.createElement('div');
      element.className = 'quiz-breed-search__no-results';
      element.textContent = message;
      this.elements.breedDropdown.replaceChildren(element);
      this.breedOptions = [];
      this.resetBreedActiveOption();
    }

    selectBreed(value, displayName, size) {
      this.state.answers.breed = value;
      this.state.answers.breedSize = size || '';

      const isMixed = value === 'mixed';
      this.elements.breedInput.value = isMixed ? '' : displayName;
      this.elements.breedInput.placeholder = isMixed
        ? this.config.i18n?.mixedSelected || ''
        : this.config.i18n?.breedPlaceholder || '';
      this.elements.breedInput.setAttribute('aria-invalid', 'false');
      this.elements.mixedBreedButton?.setAttribute('aria-pressed', String(isMixed));
      if (this.elements.breedValue) this.elements.breedValue.value = value;

      this.hideBreedDropdown();
      this.clearStepError(this.currentStepElement());

      const selectedName = isMixed ? this.config.i18n?.mixedSelected : displayName;
      this.announce(
        this.elements.breedStatus,
        interpolate(this.config.i18n?.breedSelected, { breed: selectedName })
      );
      this.track('breed_selected', {
        breed: value,
        breed_size: size || null,
        mixed: isMixed
      });
    }

    handleBreedKeydown(event) {
      const isOpen = !this.elements.breedDropdown.hidden;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen) this.showBreedResults(this.elements.breedInput.value);
          if (this.breedOptions.length > 0) this.setActiveBreedOption(this.breedActiveIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen) this.showBreedResults(this.elements.breedInput.value);
          if (this.breedOptions.length > 0) {
            this.setActiveBreedOption(this.breedActiveIndex < 0 ? this.breedOptions.length - 1 : this.breedActiveIndex - 1);
          }
          break;
        case 'Home':
          if (isOpen && this.breedOptions.length > 0) {
            event.preventDefault();
            this.setActiveBreedOption(0);
          }
          break;
        case 'End':
          if (isOpen && this.breedOptions.length > 0) {
            event.preventDefault();
            this.setActiveBreedOption(this.breedOptions.length - 1);
          }
          break;
        case 'Enter':
          if (isOpen && this.breedActiveIndex >= 0) {
            event.preventDefault();
            this.breedOptions[this.breedActiveIndex].click();
          }
          break;
        case 'Escape':
          if (isOpen) {
            event.preventDefault();
            this.hideBreedDropdown();
            this.announce(this.elements.breedStatus, this.config.i18n?.breedClosed || '');
          }
          break;
        case 'Tab':
          this.hideBreedDropdown();
          break;
      }
    }

    setBreedDropdownExpanded(expanded) {
      this.elements.breedDropdown.hidden = !expanded;
      this.elements.breedDropdown.classList.toggle('quiz-breed-search__dropdown--visible', expanded);
      this.elements.breedInput.setAttribute('aria-expanded', String(expanded));
      if (!expanded) this.resetBreedActiveOption();
    }

    hideBreedDropdown() {
      clearTimeout(this.breedSearchTimer);
      this.breedSearchTimer = null;
      this.breedRequestToken += 1;
      if (this.elements.breedDropdown && this.elements.breedInput) {
        this.setBreedDropdownExpanded(false);
      }
    }

    resetBreedActiveOption() {
      this.breedOptions.forEach(option => {
        option.classList.remove('quiz-breed-search__item--active');
        option.setAttribute('aria-selected', 'false');
      });
      this.breedActiveIndex = -1;
      this.elements.breedInput?.removeAttribute('aria-activedescendant');
    }

    setActiveBreedOption(index) {
      if (this.breedOptions.length === 0) return;

      const normalizedIndex = (index + this.breedOptions.length) % this.breedOptions.length;
      this.breedOptions.forEach((option, optionIndex) => {
        const active = optionIndex === normalizedIndex;
        option.classList.toggle('quiz-breed-search__item--active', active);
        option.setAttribute('aria-selected', String(active));
      });

      this.breedActiveIndex = normalizedIndex;
      const activeOption = this.breedOptions[normalizedIndex];
      this.elements.breedInput.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    }

    getDiagnosis() {
      const { behavior, trigger, worst } = this.state.answers;
      let key;

      if (behavior === 'self-injury' || worst === 'self-injury') {
        key = 'injury';
      } else if (
        ['bends-breaks', 'escapes-quickly'].includes(behavior) ||
        worst === 'property-damage'
      ) {
        key = 'escape';
      } else if (trigger === 'separation') {
        key = 'separation';
      } else if (trigger === 'noise') {
        key = 'noise';
      } else if (trigger === 'confinement') {
        key = 'confinement';
      } else {
        key = 'night';
      }

      const diagnosis = this.config.i18n?.diagnoses?.[key] || { title: '', copy: '' };
      return {
        key,
        title: diagnosis.title,
        copy: interpolate(diagnosis.copy, { dog: this.state.answers.dogName })
      };
    }

    getRiskLevel() {
      const { behavior, history, worst } = this.state.answers;
      const behaviorScores = {
        'bends-breaks': 3,
        'self-injury': 4,
        'escapes-quickly': 4,
        'panic-vocalizing': 1
      };
      const historyScores = {
        'first-crate': 0,
        'one-to-two': 2,
        'three-plus': 4
      };
      const worstScores = {
        'self-injury': 4,
        'property-damage': 3,
        'afraid-coming': 1,
        'noise-complaints': 1
      };
      const score = (behaviorScores[behavior] || 0) + (historyScores[history] || 0) + (worstScores[worst] || 0);

      if (score >= 8) return 'high';
      if (score >= 4) return 'elevated';
      return 'moderate';
    }

    getRecommendation() {
      const { breed, breedSize, weight } = this.state.answers;
      const breedRecommendedSize = breed && breed !== 'mixed' ? extractSize(breedSize) : null;
      const weightRecommendedSize = WEIGHT_TO_SIZE[weight];
      let size = breedRecommendedSize || weightRecommendedSize;
      let caveat = '';

      if (
        breedRecommendedSize &&
        weightRecommendedSize &&
        breedRecommendedSize !== weightRecommendedSize
      ) {
        size = weightRecommendedSize;
        caveat = interpolate(this.config.i18n?.caveatMismatch, {
          breed_size: SIZE_LABELS[breedRecommendedSize] || breedRecommendedSize,
          weight_size: SIZE_LABELS[weightRecommendedSize] || weightRecommendedSize
        });
      } else if (['20', '24'].includes(size)) {
        caveat = this.config.i18n?.caveatSmall || '';
      } else if (size === '54') {
        caveat = this.config.i18n?.caveatGiant || '';
      }

      let variant = caveat ? null : this.findVariant(size);
      if (!caveat && !variant) {
        caveat = interpolate(this.config.i18n?.caveatUnavailable, {
          size: SIZE_LABELS[size] || size || ''
        });
        variant = null;
      }
      return {
        size,
        variant,
        caveat,
        url: this.buildProductUrl(variant)
      };
    }

    findVariant(size) {
      if (!size || !Array.isArray(this.config.product?.variants)) return null;

      return this.config.product.variants.find(variant => {
        if (!variant.available) return false;
        const optionSizes = Array.isArray(variant.options)
          ? variant.options.map(option => extractSize(option))
          : [extractSize(variant.title)];
        return optionSizes.includes(size);
      }) || null;
    }

    buildProductUrl(variant) {
      const productPath = this.config.product?.url || '/products/high-anxiety-crate-xl';
      const url = new URL(productPath, window.location.origin);
      if (variant?.id) url.searchParams.set('variant', variant.id);
      url.searchParams.set('utm_source', 'anxiety_quiz');
      url.searchParams.set('utm_medium', 'retargeting');
      url.searchParams.set('utm_campaign', this.config.utmCampaign || 'anxiety-quiz-retargeting');

      const discountCode = String(this.config.discountCode || '').trim();
      if (!discountCode) return url.toString();

      const rootUrl = String(this.config.rootUrl || '/').replace(/\/?$/, '/');
      const discountUrl = new URL(`${rootUrl}discount/${encodeURIComponent(discountCode)}`, window.location.origin);
      discountUrl.searchParams.set('redirect', `${url.pathname}${url.search}${url.hash}`);
      return discountUrl.toString();
    }

    renderResults() {
      const dogName = this.state.answers.dogName;
      const diagnosis = this.state.diagnosis;
      const recommendation = this.state.recommendation;
      const sizeLabel = SIZE_LABELS[recommendation.size] || recommendation.size || '';
      const requiresSizingReview = Boolean(recommendation.caveat);
      const displayedSize = requiresSizingReview
        ? this.config.i18n?.sizingReview
        : interpolate(this.config.i18n?.recommendedSize, { size: sizeLabel });

      if (this.elements.resultsTitle) {
        this.elements.resultsTitle.textContent = interpolate(this.config.i18n?.resultsHeading, { dog: dogName });
      }
      if (this.elements.resultsIntro) {
        this.elements.resultsIntro.textContent = interpolate(this.config.i18n?.resultsIntro, { dog: dogName });
      }
      if (this.elements.diagnosisTitle) this.elements.diagnosisTitle.textContent = diagnosis.title;
      if (this.elements.diagnosisCopy) this.elements.diagnosisCopy.textContent = diagnosis.copy;
      if (this.elements.resultSize) this.elements.resultSize.textContent = displayedSize;

      if (this.elements.sizeCaveat) {
        this.elements.sizeCaveat.textContent = recommendation.caveat;
        this.elements.sizeCaveat.hidden = !recommendation.caveat;
      }

      if (this.elements.resultImage) {
        this.elements.resultImage.alt = interpolate(this.config.i18n?.imageAlt, {
          product: this.config.product?.title || 'High Anxiety Crate',
          dog: dogName
        });
      }

      if (this.elements.resultCta) {
        const ctaText = requiresSizingReview
          ? this.config.i18n?.reviewCta
          : this.config.ctaLabel || this.elements.resultCta.textContent.trim();
        this.elements.resultCta.href = recommendation.url;
        this.elements.resultCta.textContent = ctaText;
        this.elements.resultCta.setAttribute('aria-label', interpolate(this.config.i18n?.ctaAria, {
          cta: ctaText,
          product: this.config.product?.title || 'High Anxiety Crate',
          size: displayedSize
        }));
      }
    }

    async completeQuiz() {
      if (this.submitting) return;

      this.submitting = true;
      this.setSubmissionLock(true);
      this.announce(this.elements.quizStatus, this.config.i18n?.preparingResults || '');
      this.state.diagnosis = this.getDiagnosis();
      this.state.recommendation = this.getRecommendation();

      const resultData = this.publicResultData();
      const submissionId = this.state.submissionId;
      const capturePromise = this.captureInKlaviyo(submissionId);
      this.submitting = false;
      this.setSubmissionLock(false);

      this.renderResults();
      this.track('completed', resultData);
      this.showStep(RESULTS_STEP, { track: false });

      void capturePromise
        .then(() => {
          this.track('email_captured', resultData);
          this.trackMetaLead(resultData, submissionId);
        })
        .catch(error => {
          console.error('Anxiety Crate Quiz Klaviyo capture failed.', error);
          this.track('email_capture_failed', resultData);
        });
    }

    setSubmissionLock(locked) {
      if (locked) {
        this.elements.form.setAttribute('aria-busy', 'true');
        this.submissionControls = Array.from(
          this.currentStepElement()?.querySelectorAll('button, input, select, textarea') || []
        ).map(control => ({ control, disabled: control.disabled }));
        this.submissionControls.forEach(({ control }) => {
          control.disabled = true;
        });
        return;
      }

      this.elements.form.removeAttribute('aria-busy');
      this.submissionControls.forEach(({ control, disabled }) => {
        if (control.isConnected) control.disabled = disabled;
      });
      this.submissionControls = [];
    }

    publicResultData() {
      return {
        quiz_tag: String(this.config.crmTag || 'quiz-anxiety').trim() || 'quiz-anxiety',
        diagnosis: this.state.diagnosis?.key || null,
        risk_level: this.getRiskLevel(),
        recommended_product: this.config.product?.handle || 'high-anxiety-crate-xl',
        recommended_size: this.state.recommendation?.size || null,
        variant_id: this.state.recommendation?.variant?.id || null,
        sizing_review_required: Boolean(this.state.recommendation?.caveat),
        utm_campaign: this.config.utmCampaign || 'anxiety-quiz-retargeting'
      };
    }

    async captureInKlaviyo(submissionId = this.state.submissionId) {
      const answers = { ...this.state.answers };
      const email = answers.email;
      if (!email) return;
      const crmTag = String(this.config.crmTag || 'quiz-anxiety').trim() || 'quiz-anxiety';
      const recommendedProductHandle = this.config.product?.handle || 'high-anxiety-crate-xl';
      const recommendedProductName = String(
        this.config.product?.title || 'High Anxiety Crate'
      ).trim() || 'High Anxiety Crate';
      const recommendedProductImage = toAbsoluteHttpUrl(this.config.product?.image);
      const recommendedProductUrl = toAbsoluteHttpUrl(
        this.state.recommendation?.url || this.config.product?.url
      ) || toAbsoluteHttpUrl(`/products/${recommendedProductHandle}`);
      const recommendedProductProperties = {
        'Quiz - Recommended Product Name': recommendedProductName,
        'Quiz - Recommended Product URL': recommendedProductUrl
      };

      if (recommendedProductImage) {
        recommendedProductProperties['Quiz - Recommended Product Image'] = recommendedProductImage;
      }

      const properties = {
        'Quiz Type': 'Anxiety Crate Quiz',
        'Quiz Tag': crmTag,
        [crmTag]: true,
        'Quiz - Dog Name': answers.dogName,
        'Quiz - Behavior': answers.behavior,
        'Quiz - Trigger': answers.trigger,
        'Quiz - Crate History': answers.history,
        'Quiz - Breed': answers.breed,
        'Quiz - Breed Size': answers.breedSize || null,
        'Quiz - Weight': answers.weight,
        'Quiz - Worst Outcome': answers.worst,
        'Quiz - Diagnosis': this.state.diagnosis?.key,
        'Quiz - Risk Level': this.getRiskLevel(),
        'Quiz - Recommended Product': recommendedProductHandle,
        ...recommendedProductProperties,
        'Quiz - Recommended Size': this.state.recommendation?.size,
        'Quiz - Marketing Consent': answers.marketingConsent,
        'Quiz - UTM Campaign': this.config.utmCampaign || 'anxiety-quiz-retargeting'
      };

      const companyId = this.resolveKlaviyoCompanyId();
      const listId = String(this.config.klaviyo?.listId || '').trim();
      if (!companyId) throw new Error('Klaviyo client event capture is not configured.');

      await this.createKlaviyoClientEvent(companyId, email, properties, submissionId);

      if (!answers.marketingConsent) return;
      if (!listId) {
        console.warn('Anxiety Crate Quiz Klaviyo marketing subscription is not configured.');
        return;
      }

      try {
        await this.createKlaviyoSubscription(companyId, listId, email, properties);
      } catch (error) {
        console.warn('Anxiety Crate Quiz Klaviyo marketing subscription failed.', error);
      }
    }

    resolveKlaviyoCompanyId() {
      const configuredId = String(this.config.klaviyo?.companyId || '').trim();
      if (configuredId) return configuredId;

      const globalId = String(
        window.quizKlaviyoCompanyId || window.klaviyoSettings?.companyId || ''
      ).trim();
      if (globalId) return globalId;

      const onsiteScript = Array.from(document.scripts).find(script => {
        return script.src.includes('static.klaviyo.com/onsite/js/');
      });
      if (!onsiteScript) return '';

      try {
        const scriptUrl = new URL(onsiteScript.src);
        const queryId = scriptUrl.searchParams.get('company_id');
        if (queryId) return queryId;

        return scriptUrl.pathname.match(/\/onsite\/js\/([^/]+)\//)?.[1] || '';
      } catch (error) {
        return '';
      }
    }

    async createKlaviyoClientEvent(companyId, email, properties, submissionId) {
      const response = await this.fetchWithTimeout(
        `https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                properties,
                unique_id: submissionId,
                metric: {
                  data: {
                    type: 'metric',
                    attributes: {
                      name: 'Anxiety Crate Quiz Completed'
                    }
                  }
                },
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      locale: this.config.locale || null,
                      properties
                    }
                  }
                }
              }
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Klaviyo event capture failed with ${response.status}.`);
      }
    }

    async createKlaviyoSubscription(companyId, listId, email, properties) {
      const response = await this.fetchWithTimeout(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Anxiety Crate Quiz',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      locale: this.config.locale || null,
                      properties,
                      subscriptions: {
                        email: {
                          marketing: {
                            consent: 'SUBSCRIBED'
                          }
                        }
                      }
                    }
                  }
                }
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: listId
                  }
                }
              }
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Klaviyo subscription failed with ${response.status}.`);
      }
    }

    async fetchWithTimeout(url, options) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), KLAVIYO_REQUEST_TIMEOUT);
      this.requestControllers.add(controller);

      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Klaviyo request timed out.');
        }
        throw error;
      } finally {
        clearTimeout(timeout);
        this.requestControllers.delete(controller);
      }
    }

    track(eventSuffix, data = {}) {
      if (window.Shopify?.designMode) return;

      const prefix = this.config.analyticsPrefix || 'anxiety_quiz';
      const eventName = `${prefix}_${eventSuffix}`;
      const payload = {
        event_category: prefix,
        quiz_type: 'anxiety',
        ...data
      };

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...payload });
    }

    metaTrackingAllowed() {
      const customerPrivacy = window.Shopify?.customerPrivacy;
      if (!customerPrivacy) return true;

      try {
        const marketingAllowed = typeof customerPrivacy.marketingAllowed === 'function'
          ? customerPrivacy.marketingAllowed()
          : typeof customerPrivacy.userCanBeTracked === 'function'
            ? customerPrivacy.userCanBeTracked()
            : true;
        const dataSharingAllowed = typeof customerPrivacy.saleOfDataAllowed === 'function'
          ? customerPrivacy.saleOfDataAllowed()
          : typeof customerPrivacy.userDataCanBeSold === 'function'
            ? customerPrivacy.userDataCanBeSold()
            : true;

        return marketingAllowed && dataSharingAllowed;
      } catch (error) {
        console.warn('Anxiety Crate Quiz could not confirm Meta tracking consent.', error);
        return false;
      }
    }

    trackMetaLead(data = {}, submissionId = this.state.submissionId) {
      if (
        window.Shopify?.designMode ||
        !this.metaTrackingAllowed() ||
        typeof window.fbq !== 'function'
      ) {
        return false;
      }

      const parameters = {
        content_name: 'Anxiety Crate Quiz',
        content_category: 'Quiz',
        quiz_type: 'anxiety',
        quiz_tag: data.quiz_tag || 'quiz-anxiety'
      };
      const eventId = String(submissionId || '').trim();

      try {
        if (eventId) {
          window.fbq('track', 'Lead', parameters, { eventID: eventId });
        } else {
          window.fbq('track', 'Lead', parameters);
        }
        return true;
      } catch (error) {
        console.warn('Anxiety Crate Quiz Meta Lead event failed.', error);
        return false;
      }
    }

    announce(element, message) {
      if (!element || !message) return;

      element.textContent = '';
      if (this.announcementFrame) cancelAnimationFrame(this.announcementFrame);
      this.announcementFrame = requestAnimationFrame(() => {
        element.textContent = message;
        this.announcementFrame = null;
      });
    }

    reset() {
      this.track('retaken');
      this.elements.form.reset();
      this.state = createInitialState();
      this.submitting = false;
      this.started = false;
      this.root.querySelectorAll('.quiz-option').forEach(option => {
        option.classList.remove('quiz-option--selected');
      });
      this.root.querySelectorAll('[data-step-error]').forEach(error => {
        error.hidden = true;
      });
      this.root.querySelectorAll('[aria-invalid]').forEach(field => {
        field.setAttribute('aria-invalid', 'false');
      });
      this.elements.breedInput.placeholder = this.config.i18n?.breedPlaceholder || '';
      this.elements.mixedBreedButton?.setAttribute('aria-pressed', 'false');
      if (this.elements.breedValue) this.elements.breedValue.value = '';
      this.hideBreedDropdown();
      this.showStep(1);
    }

    destroy() {
      clearTimeout(this.breedSearchTimer);
      if (this.announcementFrame) cancelAnimationFrame(this.announcementFrame);
      this.requestControllers.forEach(controller => controller.abort());
      this.requestControllers.clear();
      this.abortController.abort();
      delete this.root.__anxietyCrateQuizInstance;
    }
  }

  function findRoots(context) {
    const roots = [];
    if (context instanceof Element && context.matches('[data-anxiety-crate-quiz]')) {
      roots.push(context);
    }
    context.querySelectorAll?.('[data-anxiety-crate-quiz]').forEach(root => roots.push(root));
    return roots;
  }

  function init(context = document) {
    findRoots(context).forEach(root => {
      if (!root.__anxietyCrateQuizInstance) {
        root.__anxietyCrateQuizInstance = new AnxietyCrateQuiz(root);
      }
    });
  }

  function boot() {
    init(document);
    document.addEventListener('shopify:section:load', event => init(event.target));
    document.addEventListener('shopify:section:unload', event => {
      findRoots(event.target).forEach(root => root.__anxietyCrateQuizInstance?.destroy());
    });
  }

  window.__impactAnxietyCrateQuiz = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
